import { ForwardedRef, forwardRef, MouseEvent } from 'react';
import { useText } from '../../../utils/hooks/use_text';
import { Button } from '../../button';
import { IntrinsicButtonProps } from '../../button/button.component';
import { Tooltip } from '../../tooltip';
import { useFormContext } from './form.context';

export const CellulaFormSubmitButton = forwardRef<HTMLButtonElement, IntrinsicButtonProps>(function CellulaFormSubmitButton(
	{ onClick, ...rest }: IntrinsicButtonProps,
	ref: ForwardedRef<HTMLButtonElement>,
): JSX.Element {
	const formContext = useFormContext();
	const staticTexts = useText();

	if (formContext === undefined) {
		throw new Error('Form.Error must be used within an Form.Root to access FormContext');
	}

	const isDirty = formContext.control.formState.isDirty;
	const isDisabledWhenCleanForm = formContext.disabledSubmitButtonWhenClean && !isDirty;
	const isDisabledWhenInvalidForm = formContext.disabledSubmitButtonWhenInvalid && !formContext.control.formState.isValid;

	function handleClick(e: MouseEvent<HTMLButtonElement>): void {
		if (rest.disabled) {
			e.preventDefault();

			return;
		}

		onClick?.(e);

		formContext?.setSubmitError(false);
	}

	const disabledProps =
		isDisabledWhenCleanForm || isDisabledWhenInvalidForm
			? {
					'aria-label': undefined,
					disabled: true,
					disabledText: staticTexts(
						isDisabledWhenCleanForm ? 'Component.FormSubmitButton.NotDirty' : 'Component.FormSubmitButton.Invalid',
					),
				}
			: {};

	const submitButton = (
		<Button
			{...rest}
			type='submit'
			loading={formContext.control.formState.isSubmitting}
			allowPassThrough
			onClick={handleClick}
			ref={ref}
			{...disabledProps}
		/>
	);

	return (
		<>
			{isDisabledWhenCleanForm ? (
				<Tooltip content={staticTexts('Component.FormSubmitButton.NotDirty')}>{submitButton}</Tooltip>
			) : (
				submitButton
			)}
		</>
	);
});
